import { ChangeEvent, KeyboardEventHandler, SyntheticEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import './SecuritySearchModal.css'
import { Box, Dialog, DialogContent, Divider, InputAdornment, Stack, styled, TextField, Typography } from '@mui/material'
import { Security, SecurityCategory, SecurityCategory_CategoryCode, SecurityExpression } from '../../proto/generated/security'
import { exchangeToJSON } from '../../proto/generated/common'
import SecurityCategoryItem from '../securityCategoryItem/SecurityCategoryItem'
import { getCategories, lookupSymbols } from '../../services/apis/securities'
import { selectUserSecurityExpressions } from '../../selectors/security'
import {
    cleanUpBadParenthesis,
    convertDisplayExpressionToExpression,
    convertExpressionToDisplayExpression,
    isSecurityExpressionTerm,
    getHighlightRange,
    getOperandRangeAtSelection,
    getOperatorIndices,
    getOperatorRegex,
    getSearchTermForSelection,
    operandEndingRegex,
    replaceNamesWithExpressions
} from '../../util/expression'
import { ExpressionCallbackData, ExpressionType } from './typings'
import OperatorsPanel from '../operatorsPanel/OperatorsPanel'
import { getExchangeAndSymbolFromKey, getSecurityAsync, getSecurityKey } from '../../util/security'
import commonActions from '../../actions/common.actions'
import { selectCurrentTheme, selectMultiPaneFullScreen, selectRecentExpressions } from '../../selectors/common'
import { deleteSecurityExpression } from '../../services/apis/expression'
import SecurityInfiniteScroll from './SecurityInfiniteScroll'
import SecurityInstrumentTypeFilter from './SecurityInstrumentTypeFilter'
import { deleteUserExpressions } from '../../reducer/security.reducer'
import { selectActiveLayoutId, selectPrimarySecurity, selectSecuritySearchOpen } from '../../selectors/home'
import { EXP_FILTER_DELIMTER, MULTIPANE_FULLSCREEN_MODE_ID } from '../../assets/constants'
import SearchIcon from '../../assets/icons/SearchIcon'
import { v4 as uuid } from 'uuid'
import useSecurityExpressionToName from '../../hooks/useExpressionNames'
import ClearIcon from '../../assets/icons/ClearIcon'
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import { ExtraSmallButton } from '../custom'
import { selectLayoutWidgets } from '../../selectors/widgetDashboard'
import { selectActiveChartId } from '../../selectors/chart'
import {
    filterCategoriesForFoUnderlyingSearch,
    isFoUnderlyingSearchContext,
} from '../../util/foUnderlyingSearch'

interface Props {
    open: boolean
    enableExpressions: boolean
    onCloseClicked: () => void
    deleteIcon?: boolean
    onSecuritySelected: Function
    selectedItems?: Set<string>
    tailIcon?: boolean
    watchlistMode?: boolean
    forceCategoryCode?: SecurityCategory_CategoryCode;
    showOnlySimulatorSecurities?: boolean
    showOnlyFOSecurities?: boolean
    compareSecurityPicker?: boolean
}

const SearchDialog = styled(Dialog)(() => ({
    '& .MuiDialog-paper': {
        minHeight: '70vh',
        maxHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    zIndex: 13000
}))

const SecuritySearchModal = (props: Props) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const darkTheme = useSelector(selectCurrentTheme)
    const {open, onCloseClicked, onSecuritySelected, deleteIcon, tailIcon, selectedItems, enableExpressions, watchlistMode, forceCategoryCode, showOnlySimulatorSecurities = false, showOnlyFOSecurities, compareSecurityPicker} = props
    const securitySearchOpen = useSelector(selectSecuritySearchOpen)
    const { key, expressionMode: expressionEnabled, primary } = useSelector(selectPrimarySecurity)
    const security = expressionEnabled ? null : primary
    const { expressionSecurities } = useSelector(selectPrimarySecurity)
    const globalExpressionToName = useSecurityExpressionToName()
    const [inputValue, setInputValue] = useState<string>('')
    const inputValueRef = useRef(inputValue)
    inputValueRef.current = inputValue
    /** Caret/selection to apply once a programmatic value change has reached the DOM (see setValueAndSelection). */
    const pendingSelection = useRef<{ start: number; end: number } | 'all' | null>(null)
    const initialValueApplied = useRef(false)
    const symbolsUsed = useRef<Record<string, Security>>(
        expressionSecurities.reduce(
            (acc: Record<string, Security>, sec: Security) => {
                acc[`${exchangeToJSON(sec.exchange)}:${sec.symbol}`] = sec
                return acc
            },
            {} as Record<string, Security>
        )
    )
    const [searchTerm, setSearchTerm] = useState<string>('')
    const cachedSecurities = useRef<Security[]>([])
    const cachedStrategyExpressions = useRef<SecurityExpression[]>([])
    const [securityCategories, setSecurityCategories] = useState<SecurityCategory[]>()
    const [selectedCategoryCode, setSelectedCategoryCode] = useState<SecurityCategory_CategoryCode>(SecurityCategory_CategoryCode.ALL)
    const [selectedInstrument, setSelectedInstrument] = useState<string | undefined>(undefined)
    const nameToDisplayExpression = useRef<Map<string, string>>(new Map())
    const displayExpressionToName = useRef<Map<string, string>>(new Map())
    const savedExpressions: SecurityExpression[] | undefined = useSelector(selectUserSecurityExpressions)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const widgets = useSelector(selectLayoutWidgets(activeLayoutId))
    const viewKey = useSelector(selectActiveChartId)
    const currentWidget = useMemo(() => widgets[Number(viewKey?.split('-')[1]) ?? 0], [viewKey, widgets])
    const recentExpressions = useSelector(selectRecentExpressions)

    const allExpressions = useMemo(() => {
        if (!enableExpressions) {
            return []
        }
        return (savedExpressions?.filter((e) => e.securities?.length > 0 && !e.expired) ?? [])
            .concat(recentExpressions)
            .reduce((unique: SecurityExpression[], e) => {
                if (!unique.some((item) => item.expression === e.expression)) {
                    unique.push(e)
                }
                return unique
            }, [])
            .map((e) => {
                const displayExpression = convertExpressionToDisplayExpression(e.expression, e.securities)
                if (e.name) {
                    nameToDisplayExpression.current.set(e.name, displayExpression)
                    displayExpressionToName.current.set(displayExpression, e.name)
                }
                return {
                    id: e.id ?? e.expression,
                    name: e.name ?? convertExpressionToDisplayExpression(e.expression, e.securities),
                    expression: e.expression,
                    securities: e.securities,
                    expired: false
                }
            })
    }, [savedExpressions, recentExpressions])
    const [focusedIndex, setFocusedIndex] = useState<number>(NaN)
    const focusedIndexRef = useRef(focusedIndex)
    focusedIndexRef.current = focusedIndex
    const maxItemsFocusable = useRef<number>(0)
    const comparePickerSubmitRef = useRef<(() => void) | null>(null)

    // A saved strategy name or any operator switches the box into expression (spread) editing.
    // `allExpressions` (re)populates nameToDisplayExpression while rendering, hence the dependency.
    const expressionMode = useMemo(() => {
        if (!enableExpressions) return false
        return allExpressions.some((exp) => exp.name === inputValue) || nameToDisplayExpression.current.has(inputValue) || getOperatorRegex().test(inputValue)
    }, [enableExpressions, inputValue, allExpressions])

    /**
     * Filter the list by the operand under the caret (or by the whole text when expressions are disabled).
     * Skipped while a row is keyboard-focused: its symbol is only being previewed in the box.
     */
    const syncSearchTermToSelection = useCallback(
        (input: HTMLInputElement) => {
            if (!Number.isNaN(focusedIndexRef.current)) return
            const value = input.value.toUpperCase()
            if (!enableExpressions) {
                const [, symbol] = getExchangeAndSymbolFromKey(value)
                setSearchTerm(symbol.trim())
                return
            }
            const start = input.selectionStart ?? value.length
            const end = input.selectionEnd ?? start
            setSearchTerm(getSearchTermForSelection(value, start, end, nameToDisplayExpression.current))
        },
        [enableExpressions]
    )

    const applyPendingSelection = useCallback(() => {
        const input = inputRef.current
        const pending = pendingSelection.current
        if (!input || !pending) return
        pendingSelection.current = null
        input.focus()
        if (pending === 'all') {
            input.select()
        } else {
            const length = input.value.length
            input.setSelectionRange(Math.min(pending.start, length), Math.min(pending.end, length))
        }
        syncSearchTermToSelection(input)
    }, [syncSearchTermToSelection])

    /**
     * Set the box's text together with where the caret (or selection) must land. React rewrites the DOM value
     * on commit, which throws the caret to the end, so the selection is applied in a layout effect right after.
     */
    const setValueAndSelection = useCallback(
        (nextValue: string, selection: { start: number; end: number } | 'all') => {
            pendingSelection.current = selection
            if (nextValue === inputValueRef.current) {
                // Nothing to commit. Defer past React's own controlled-value restore (which follows the event handler).
                queueMicrotask(applyPendingSelection)
                return
            }
            setInputValue(nextValue)
        },
        [applyPendingSelection]
    )

    // Runs right after each committed value change, before paint, so the caret never flashes at the end.
    useLayoutEffect(() => {
        applyPendingSelection()
    }, [inputValue, applyPendingSelection])

    /** Arrow-key navigation previews the focused row's symbol in the box, caret at the end. */
    const previewInputValue = useCallback(
        (value: string) => {
            setValueAndSelection(value, { start: value.length, end: value.length })
        },
        [setValueAndSelection]
    )

    const dispatch = useDispatch()
    const foUnderlyingSearch = isFoUnderlyingSearchContext({
        showOnlyFOSecurities,
        watchlistMode,
        widgetType: currentWidget?.widgetType,
    })

    useEffect(() => {
        if (!open) {
            return
        }
        let cancelled = false
        ;(async () => {
            const securityCategoriesResponse = await getCategories()
            if (cancelled) {
                return
            }
            let categories = securityCategoriesResponse.categories
            // Select-underlying: All / Stocks / Index only (cash underlyings with hasFo).
            if (foUnderlyingSearch) {
                categories = filterCategoriesForFoUnderlyingSearch(categories)
            }
            setSecurityCategories(categories)
            if (foUnderlyingSearch) {
                setSelectedCategoryCode(SecurityCategory_CategoryCode.ALL)
                setSelectedInstrument(undefined)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [open, foUnderlyingSearch])

    function onCategorySelected(code: SecurityCategory_CategoryCode) {
        if (forceCategoryCode) return;
        setSelectedCategoryCode(code)
        setSelectedInstrument(undefined)
    }

    const selectedCategory = useMemo(
        () => (securityCategories ?? []).find((category) => category.code === selectedCategoryCode),
        [securityCategories, selectedCategoryCode]
    )
    // Charts: type filter when 2+ subtypes. FO underlying picker: no subtypes (TV-style).
    const categorySubtypes = useMemo(() => {
        if (foUnderlyingSearch) {
            return []
        }
        if (
            selectedCategoryCode === SecurityCategory_CategoryCode.ALL ||
            selectedCategoryCode === SecurityCategory_CategoryCode.SPREADS
        ) {
            return []
        }
        const subtypes = selectedCategory?.subtypes ?? []
        return subtypes.length > 1 ? subtypes : []
    }, [foUnderlyingSearch, selectedCategory, selectedCategoryCode])

    // Prefill the box with the current symbol/expression once, fully selected so typing replaces it.
    useEffect(() => {
        if (initialValueApplied.current || inputValueRef.current.length > 0) return
        let cancelled = false
        const applyInitialValue = (value: string) => {
            if (cancelled || !value || inputValueRef.current.length > 0) return
            initialValueApplied.current = true
            setValueAndSelection(value, 'all')
        }
        if (securitySearchOpen.key) {
            if (securitySearchOpen.isExpression) {
                const backendKey = key || securitySearchOpen.key
                applyInitialValue(globalExpressionToName.get(backendKey) ?? convertExpressionToDisplayExpression(backendKey, expressionSecurities))
            } else {
                getSecurityAsync(securitySearchOpen.key).then((security) => {
                    if (security?.symbol) applyInitialValue(getSecurityKey(security.exchange, security.symbol))
                })
            }
        } else if (enableExpressions && expressionEnabled) {
            applyInitialValue(globalExpressionToName.get(key) ?? convertExpressionToDisplayExpression(key, expressionSecurities))
        } else if (security) {
            applyInitialValue(`${exchangeToJSON(security.exchange)}:${security.symbol}`)
        }
        return () => {
            cancelled = true
        }
    }, [
        enableExpressions,
        expressionEnabled,
        security,
        key,
        expressionSecurities,
        globalExpressionToName,
        securitySearchOpen.key,
        securitySearchOpen.isExpression,
        setValueAndSelection
    ])

    const onInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const input = event.target
            const raw = input.value
            // Leave text alone mid-IME-composition; the composed result arrives in a later change event.
            const value = (event.nativeEvent as InputEvent).isComposing ? raw : raw.toUpperCase()
            // Keep the caret where the browser put it. Positions go through toUpperCase too, as it can change length (ß → SS).
            const caretStart = raw.slice(0, input.selectionStart ?? raw.length).toUpperCase().length
            const caretEnd = raw.slice(0, input.selectionEnd ?? input.selectionStart ?? raw.length).toUpperCase().length
            setFocusedIndex(NaN)
            setValueAndSelection(value, { start: caretStart, end: caretEnd })
        },
        [setValueAndSelection]
    )

    // Bound to onSelect and onClick: the click covers a caret placed by collapsing an existing selection.
    const onInputSelect = useCallback(
        (event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            syncSearchTermToSelection(event.currentTarget as HTMLInputElement)
        },
        [syncSearchTermToSelection]
    )

    // React's onSelect misses a click that collapses an existing selection (mouseup still reports the old
    // range; the caret arrives in a later selectionchange). Track the document's selectionchange directly.
    useEffect(() => {
        if (!open) return
        const onSelectionChange = () => {
            const input = inputRef.current
            if (input && document.activeElement === input) syncSearchTermToSelection(input)
        }
        document.addEventListener('selectionchange', onSelectionChange)
        return () => document.removeEventListener('selectionchange', onSelectionChange)
    }, [open, syncSearchTermToSelection])

    const onExpressionSelected = useCallback(
        (expression: SecurityExpression, selected: boolean, securities: Security[], type: ExpressionType) => {
            const securityExpression = allExpressions.find((exp) => exp.expression === expression?.expression)
            if (!securities && !securityExpression) {
                commonActions.setApiError(dispatch, { error: true, message: 'Something went wrong', errorType: 'error' })
                return
            }
            onSecuritySelected(
                {
                    expression: cleanUpBadParenthesis(expression.expression, false),
                    securities: securities ?? securityExpression?.securities,
                    expressionMode: true,
                    expressionId: expression.id ?? cleanUpBadParenthesis(expression.expression, false),
                    type: type,
                    name: expression.name
                },
                selected
            )
        },
        [onSecuritySelected, allExpressions, dispatch]
    )

    const onExpressionDeleted = useCallback(
        (expression: string) => {
            const securityExpression = (savedExpressions ?? []).find((exp) => exp.expression === expression)
            commonActions.deleteRecentExpression(dispatch, expression)
            if (securityExpression) {
                deleteSecurityExpression({ expression: securityExpression }).then(() => {
                    dispatch(deleteUserExpressions(securityExpression))
                })
            }
        },
        [savedExpressions, dispatch]
    )

    const onSecuritySelect = useCallback(
        (data: Security | SecurityExpression, selected: boolean, focused: boolean, type: ExpressionType | 'SECURITY') => {
            const isExpression = ['EXPRESSION', 'STRATEGY_EXPRESSION'].includes(type)
            if (!expressionMode || (expressionMode && isExpression)) {
                if (isExpression) {
                    onExpressionSelected(
                        focused
                            ? {
                                  ...(data as SecurityExpression),
                                  expression: cleanUpBadParenthesis((data as SecurityExpression).expression)
                              }
                            : (data as SecurityExpression),
                        selected,
                        (data as SecurityExpression).securities,
                        type as ExpressionType
                    )
                } else {
                    onSecuritySelected({ expression: '', securities: [data], expressionMode: false }, selected)
                }
                return
            }
            // Replace the explicit selection, or the operand under the caret, with the picked symbol.
            const input = inputRef.current
            let start = isExpression ? 0 : (input?.selectionStart ?? inputValue.length)
            let end = isExpression ? inputValue.length : (input?.selectionEnd ?? start)
            if (end === start) {
                ;[start, end] = getOperandRangeAtSelection(inputValue, start, end)
            }

            let term
            if (isExpression) {
                const displayExpression = convertExpressionToDisplayExpression((data as SecurityExpression).expression, (data as SecurityExpression).securities)
                term = displayExpressionToName.current.get(displayExpression) ?? displayExpression
                ;(data as SecurityExpression).securities?.forEach((sec) => {
                    symbolsUsed.current[(exchangeToJSON(sec.exchange) || 'NSE') + ':' + sec.symbol] = sec
                })
            } else {
                term = (exchangeToJSON((data as Security).exchange) || 'NSE') + ':' + (data as Security).symbol
                symbolsUsed.current[term] = data as Security
            }
            const caret = start + term.length
            setValueAndSelection(inputValue.substring(0, start) + term + inputValue.substring(end), { start: caret, end: caret })
        },
        [expressionMode, inputValue, onExpressionSelected, onSecuritySelected, setValueAndSelection]
    )

    // Operators apply to the top-level operand under the caret; a selected sub-expression is parenthesised first.
    const onOperatorClick = useCallback(
        (value: string) => {
            const input = inputRef.current
            const selectionStart = input?.selectionStart ?? inputValue.length
            const selectionEnd = input?.selectionEnd ?? selectionStart
            const [start, end] = getHighlightRange(inputValue, selectionStart, selectionEnd)
            let operand = inputValue.substring(start, end)
            if (getOperatorRegex().test(operand)) {
                operand = cleanUpBadParenthesis(operand, true)
            }
            operand = value === '1/' ? `1/${operand}` : operand + value
            const caret = start + operand.length
            setValueAndSelection(inputValue.substring(0, start) + operand + inputValue.substring(end), { start: caret, end: caret })
        },
        [inputValue, setValueAndSelection]
    )

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
        async (e) => {
            if (e.key === 'ArrowDown') {
                if (Number.isNaN(focusedIndex)) {
                    setFocusedIndex(0)
                } else if (focusedIndex + 1 <= maxItemsFocusable.current - 1) {
                    setFocusedIndex(focusedIndex + 1)
                }
                e.preventDefault()
            } else if (e.key === 'ArrowUp') {
                if (Number.isNaN(focusedIndex)) {
                    setFocusedIndex(0)
                } else if (focusedIndex === 0) {
                    setFocusedIndex(NaN)
                } else {
                    setFocusedIndex(focusedIndex - 1)
                }
                e.preventDefault()
            }
        },
        [focusedIndex]
    )

    const onKeyUp: KeyboardEventHandler<HTMLInputElement> = useCallback(
        async (e) => {
            if (e.key === 'Enter') {
                if (compareSecurityPicker && !expressionMode && !Number.isNaN(focusedIndex)) {
                    comparePickerSubmitRef.current?.()
                    e.preventDefault()
                    return
                }
                const rawInputValue = replaceNamesWithExpressions(inputValue, nameToDisplayExpression.current)
                if (operandEndingRegex.test(rawInputValue)) {
                    return
                }
                const indices = getOperatorIndices(rawInputValue)
                const securitiesToFetch = new Set<string>()
                const invalidSymbols = new Set<string>()
                const securities: Security[] = []
                indices.forEach(([start, end]) => {
                    if (end > start) {
                        const term = rawInputValue.substring(start, end + 1).trim()
                        if (isSecurityExpressionTerm(term)) {
                            let [code] = term.trim().split(EXP_FILTER_DELIMTER)
                            code = code.trim()
                            const sec = symbolsUsed.current[code]

                            if (!sec || sec.categoryCode === undefined) {
                                if (term.indexOf(':') === -1) {
                                    invalidSymbols.add(code)
                                } else {
                                    securitiesToFetch.add(code)
                                }
                            } else {
                                securities.push(sec)
                            }
                        }
                    }
                })
                if (invalidSymbols.size > 0) {
                    return
                }
                if (securitiesToFetch.size > 0) {
                    try {
                        const response = await lookupSymbols(
                            Array.from(securitiesToFetch).map((sec) => {
                                const [exchange, symbol] = getExchangeAndSymbolFromKey(sec)
                                return {
                                    exchange,
                                    symbol
                                }
                            })
                        )
                        if (response && response.securities.length) {
                            response.securities.forEach((sec) => {
                                symbolsUsed.current[`${exchangeToJSON(sec.exchange)}:${sec.symbol}`] = sec
                                securities.push(sec)
                            })
                        }
                    } catch (e) {
                        console.error(e)
                    }
                }
                let finalExpression = convertDisplayExpressionToExpression(rawInputValue, securities)

                if (selectedItems && selectedItems?.has(finalExpression)) {
                    commonActions.setApiError(dispatch, {
                        error: true,
                        message: 'Expression already added',
                        errorType: 'error'
                    })
                    return
                }
                const data: ExpressionCallbackData = {
                    expression: cleanUpBadParenthesis(finalExpression, false),
                    expressionId: uuid(),
                    securities: securities,
                    expressionMode: true,
                    type: 'EXPRESSION'
                }
                if (securities.length === 1 && finalExpression === getSecurityKey(securities[0].exchange, securities[0].code)) {
                    data.expression = ''
                    data.expressionMode = false
                    data.type = undefined
                } else if (displayExpressionToName.current.has(rawInputValue)) {
                    data.name = displayExpressionToName.current.get(rawInputValue)!
                    if (!new Set(allExpressions.map((exp) => exp.name)).has(data.name)) {
                        data.type = 'STRATEGY_EXPRESSION'
                    }
                }
                onSecuritySelected(data, true)
            }
        },
        [inputValue, selectedItems, onSecuritySelected, dispatch, allExpressions, compareSecurityPicker, expressionMode, focusedIndex]
    )

    const onMaxFocusableChange = useCallback((max: number) => {
        maxItemsFocusable.current = max
    }, [])

    const reduxContainer = useSelector(selectMultiPaneFullScreen)
    // Use native fullscreen element (covers option-chain fullscreen) OR fall back to Redux multipane fullscreen
    const nativeFullscreenEl = useSyncExternalStore(
        (cb) => { document.addEventListener('fullscreenchange', cb); return () => document.removeEventListener('fullscreenchange', cb) },
        () => document.fullscreenElement as HTMLElement | null,
        () => null
    )
    const container = nativeFullscreenEl ?? (reduxContainer ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null)

    return (
        <SearchDialog
            open={open}
            onClose={onCloseClicked}
            aria-labelledby="customized-dialog-title"
            maxWidth={'md'}
            container={container ?? undefined}
            fullWidth
        >
            <DraggableDialogTitle id="customized-dialog-title">
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                    <Typography variant={'h6'}>{foUnderlyingSearch ? 'Underlying Search' : 'Symbol Search'}</Typography>
                    <ExtraSmallButton onClick={onCloseClicked} aria-label="close" color={'inherit'}>
                        <ClearIcon fontSize={'small'} />
                    </ExtraSmallButton>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        position: 'relative',
                        marginBottom: 1
                    }}
                >
                    <TextField
                        variant="standard"
                        fullWidth
                        inputRef={inputRef}
                        multiline={false}
                        placeholder="Search here..."
                        value={inputValue}
                        disabled={showOnlySimulatorSecurities}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 24, opacity: 0.5 }} />
                                </InputAdornment>
                            ),
                            disableUnderline: true
                        }}
                        inputProps={{
                            onSelect: onInputSelect,
                            onClick: onInputSelect,
                            onKeyDown,
                            onKeyUp,
                            spellCheck: false,
                            autoCapitalize: 'characters'
                        }}
                        sx={{
                            // The dialog title around this box is the drag handle (grab cursor, no text selection);
                            // the box itself must still look and behave like a text field.
                            '& .MuiInputBase-root': { cursor: 'text' },
                            '& .MuiInputBase-input': { cursor: 'text', userSelect: 'text' }
                        }}
                        onChange={onInputChange}
                        autoFocus
                        autoComplete="off"
                    />
                    {(enableExpressions && !showOnlySimulatorSecurities) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '24px', flexGrow: 1 }}>
                            <Divider orientation="vertical" sx={{ marginRight: (theme) => theme.spacing(1) }} />
                            <OperatorsPanel onClick={(value) => onOperatorClick(value)} enabled={true} />
                        </Box>
                    )}
                </Box>
            </DraggableDialogTitle>
            {!forceCategoryCode && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        gap: 1,
                        px: 3,
                        pb: 1.5,
                        pt: 0,
                        minHeight: 44,
                        flexShrink: 0,
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        flexWrap="nowrap"
                        useFlexGap
                        spacing={0.5}
                        sx={{
                            flex: '1 1 auto',
                            minWidth: 0,
                            overflowX: 'auto',
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                        }}
                    >
                        {(securityCategories ?? []).map((category) => (
                                <SecurityCategoryItem
                                    key={category.code}
                                    onSelected={onCategorySelected}
                                    selected={selectedCategoryCode === category.code}
                                    category={category}
                                    darkTheme={darkTheme}
                                    inputRef={inputRef}
                                />
                            ))}
                    </Stack>
                    {!foUnderlyingSearch && (
                        <SecurityInstrumentTypeFilter
                            subtypes={categorySubtypes}
                            selectedInstrument={selectedInstrument}
                            menuContainer={container}
                            menuZIndex={14000}
                            onChange={(instrument) => {
                                setSelectedInstrument(instrument)
                                inputRef.current?.focus()
                            }}
                        />
                    )}
                </Box>
            )}
            <DialogContent sx={{ padding: 0, flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
                <SecurityInfiniteScroll
                    categoryCode={forceCategoryCode ?? selectedCategoryCode}
                    instrument={categorySubtypes.length > 0 ? selectedInstrument : undefined}
                    searchText={searchTerm || ''}
                    tailIcon={tailIcon}
                    deleteIcon={deleteIcon}
                    expressions={allExpressions}
                    cachedSecurities={cachedSecurities}
                    cachedStrategyExpressions={cachedStrategyExpressions}
                    highlightSelected={watchlistMode || false}
                    expressionToName={displayExpressionToName}
                    nameToExpression={nameToDisplayExpression}
                    onSecuritySelect={onSecuritySelect}
                    onExpressionDeleted={onExpressionDeleted}
                    selectedItems={selectedItems}
                    setInputValue={previewInputValue}
                    focusedIndex={focusedIndex}
                    onMaxFocusableChange={onMaxFocusableChange}
                    expressionMode={expressionMode}
                    showOnlySimulatorSecurities={showOnlySimulatorSecurities}
                    showOnlyFOSecurities={foUnderlyingSearch}
                    watchlistMode={watchlistMode}
                    compareSecurityPicker={compareSecurityPicker}
                    comparePickerSubmitRef={comparePickerSubmitRef}
                    onCompareSecurityPick={
                        compareSecurityPicker
                            ? (security, placement) => {
                                  onSecuritySelected(
                                      {
                                          expression: '',
                                          securities: [security],
                                          expressionMode: false,
                                          comparePlacement: placement
                                      },
                                      true
                                  )
                              }
                            : undefined
                    }
                />
            </DialogContent>
        </SearchDialog>
    )
}

export default SecuritySearchModal
